import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, CreditCard, Check, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/utils/api";

const CheckoutPage = () => {
  const { certificateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMethod, setProcessingMethod] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [pricing, setPricing] = useState({
    currency: "USD",
    amount: 0.89,
    original_amount: 1.89,
    symbol: "$",
    payment_gateway: "paypal"
  });
  const [error, setError] = useState(null);

  // Calculate discount
  const discount = pricing.original_amount - pricing.amount;

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check for PayPal return
    const paymentId = searchParams.get('paymentId');
    const PayerID = searchParams.get('PayerID');
    
    if (paymentId && PayerID) {
      handlePayPalReturn(paymentId, PayerID);
    } else {
      fetchCertificateAndPricing();
    }
  }, [certificateId, searchParams]);

  const handlePayPalReturn = async (paymentId, PayerID) => {
    setIsProcessing(true);
    setProcessingMethod('paypal');
    try {
      const response = await axios.get(
        `${API}/paypal/execute?paymentId=${paymentId}&PayerID=${PayerID}&certificate_id=${certificateId}`
      );
      
      if (response.data.status === "success") {
        toast.success("Payment successful!");
        navigate(`/proof/${response.data.certificate_id}`);
      } else {
        toast.error("Payment already processed");
        navigate(`/proof/${certificateId}`);
      }
    } catch (err) {
      console.error("PayPal capture error:", err);
      toast.error(err.response?.data?.detail || "Payment verification failed");
      setError("Payment verification failed. Please contact support.");
      setIsProcessing(false);
      setProcessingMethod(null);
      fetchCertificateAndPricing();
    }
  };

  const fetchCertificateAndPricing = async () => {
    try {
      const certResponse = await axios.get(`${API}/certificates/${certificateId}/preview`);
      setCertificate(certResponse.data);
      
      if (certResponse.data.is_paid) {
        navigate(`/proof/${certificateId}`);
        return;
      }
      
      const geoResponse = await axios.get(`${API}/geolocation`);
      setPricing(geoResponse.data);
      
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.response?.data?.detail || "Failed to load checkout. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    setIsProcessing(true);
    setProcessingMethod('paypal');
    
    try {
      const orderResponse = await axios.post(`${API}/paypal/create-order`, {
        certificate_id: certificateId
      });
      
      const orderData = orderResponse.data;
      
      if (orderData.approval_url) {
        toast.info("Redirecting to PayPal...");
        window.location.href = orderData.approval_url;
      } else {
        throw new Error("Failed to get PayPal approval URL");
      }
    } catch (error) {
      console.error("PayPal error:", error);
      toast.error(error.response?.data?.detail || "PayPal payment failed. Please try again.");
      setIsProcessing(false);
      setProcessingMethod(null);
    }
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setProcessingMethod('card');
    
    try {
      if (pricing.payment_gateway === "razorpay" || pricing.currency === "INR") {
        // Razorpay for India
        const orderResponse = await axios.post(`${API}/payments/create-order`, {
          certificate_id: certificateId,
          currency: pricing.currency
        });
        
        const orderData = orderResponse.data;
        
        if (orderData.mock) {
          toast.info("Demo mode: Simulating payment...");
          await axios.post(`${API}/payments/mock-success?certificate_id=${certificateId}`);
          toast.success("Payment successful!");
          navigate(`/proof/${certificateId}`);
          return;
        }
        
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);
        
        script.onload = () => {
          const options = {
            key: orderData.key,
            amount: orderData.amount,
            currency: orderData.currency,
            name: "LoBe Love",
            description: "Love Certificate Payment",
            order_id: orderData.order_id,
            handler: async function (response) {
              try {
                await axios.post(`${API}/payments/verify`, {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  certificate_id: certificateId
                });
                
                toast.success("Payment successful!");
                navigate(`/proof/${certificateId}`);
              } catch (error) {
                toast.error("Payment verification failed. Please contact support.");
              }
            },
            prefill: { name: certificate?.your_name || "" },
            theme: { color: "#E8A0BF" },
            modal: {
              ondismiss: function() {
                setIsProcessing(false);
                setProcessingMethod(null);
                toast.error("Payment cancelled");
              }
            }
          };
          
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        };
      } else {
        // For international, redirect to PayPal
        handlePayPalPayment();
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment failed. Please try again.");
      setIsProcessing(false);
      setProcessingMethod(null);
    }
  };

  // Format currency
  const formatCurrency = (amount, showCurrencyCode = false) => {
    if (pricing.currency === "INR") {
      return `₹${amount.toFixed(2)}`;
    }
    return showCurrencyCode ? `$${amount.toFixed(2)} USD` : `$${amount.toFixed(2)}`;
  };

  // Get payment gateway display name
  const getPaymentGateway = () => {
    return pricing.payment_gateway === "razorpay" ? "Razorpay" : "PayPal";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-[#E8A0BF] animate-pulse mx-auto mb-4" />
          <p className="text-[#5A5A5A]">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-[#2D2D2D] mb-2">Oops!</h2>
          <p className="text-[#5A5A5A] mb-6">{error}</p>
          <Button onClick={() => navigate("/create")} className="bg-[#E8A0BF] hover:bg-[#D68C85]">
            Create New Certificate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          
          {/* Left Column - Product Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gradient-to-br from-[#e8f4f8] to-[#f0f7fa] rounded-2xl p-6 lg:p-8"
          >
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2D2D2D] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Love Certificate
            </h1>
            
            {/* Discount Badge + Pricing */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-sm text-green-600 font-medium border border-green-100">
                <Tag className="w-4 h-4" />
                {formatCurrency(discount)} discount
              </span>
              <span className="text-gray-400 line-through text-lg">
                {formatCurrency(pricing.original_amount)}
              </span>
              <span className="text-2xl lg:text-3xl font-bold text-[#2D2D2D]">
                {formatCurrency(pricing.amount)}
              </span>
            </div>
            
            <p className="text-[#5A5A5A] mb-6 leading-relaxed text-sm lg:text-base">
              A permanent, timestamped proof of your bond. Create a beautiful certificate that captures your love story forever.
            </p>

            {/* Certificate Preview Card */}
            {certificate && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#E8A0BF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-[#E8A0BF]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2D2D2D]">
                      {certificate.your_name} & {certificate.partner_name}
                    </p>
                    <p className="text-sm text-[#888888] font-mono">
                      {certificate.certificate_id}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* What's Included */}
            <div>
              <h3 className="font-semibold text-[#2D2D2D] mb-3 italic">What's included:</h3>
              <ul className="space-y-2.5">
                {[
                  "High-quality PNG certificate",
                  "Unique shareable preview page",
                  "Permanent timestamped record",
                  "Instant download access"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm text-[#5A5A5A]">
                    <span className="w-5 h-5 bg-green-50 rounded-full flex items-center justify-center text-green-600 flex-shrink-0">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right Column - Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:sticky lg:top-8 self-start"
          >
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 border border-gray-100">
              <h2 className="text-xl lg:text-2xl font-semibold text-[#2D2D2D] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Order summary
              </h2>

              {/* Price Breakdown */}
              <div className="border border-gray-200 rounded-xl p-5 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[#5A5A5A]">Subtotal</span>
                  <span className="text-[#2D2D2D] font-medium">{formatCurrency(pricing.original_amount)}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-[#5A5A5A]">Discount(s)</span>
                  <span className="text-green-600 font-medium">-{formatCurrency(discount)}</span>
                </div>
                <div className="border-t border-gray-200 pt-4 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[#2D2D2D] font-semibold text-lg">Total</span>
                    <span className="text-[#2D2D2D] font-bold text-xl lg:text-2xl">{formatCurrency(pricing.amount)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Gateway Info */}
              <p className="text-center text-sm text-[#888888] mb-5">
                Secure payment via {getPaymentGateway()}
              </p>

              {/* Main Payment Button */}
              <Button
                onClick={handlePayment}
                disabled={isProcessing}
                data-testid="proceed-to-pay-btn"
                className="w-full h-14 lg:h-16 bg-[#E8A0BF] hover:bg-[#d890af] text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-base lg:text-lg shadow-md hover:shadow-lg"
              >
                {isProcessing ? (
                  <span className="animate-pulse">Processing...</span>
                ) : (
                  <>
                    <Heart className="w-5 h-5" />
                    <span>Proceed to Pay {formatCurrency(pricing.amount)}</span>
                  </>
                )}
              </Button>

              {/* Payment Methods Icons */}
              <div className="flex items-center justify-center gap-3 mt-5">
                {/* PayPal */}
                <div className="w-10 h-6 bg-[#003087] rounded flex items-center justify-center">
                  <span className="text-white text-xs font-bold">P</span>
                </div>
                {/* Visa */}
                <div className="w-10 h-6 bg-[#1a1f71] rounded flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold italic">VISA</span>
                </div>
                {/* Mastercard */}
                <div className="w-10 h-6 bg-gradient-to-r from-[#eb001b] to-[#f79e1b] rounded flex items-center justify-center">
                  <div className="flex">
                    <div className="w-2.5 h-2.5 bg-[#eb001b] rounded-full opacity-80"></div>
                    <div className="w-2.5 h-2.5 bg-[#f79e1b] rounded-full -ml-1 opacity-80"></div>
                  </div>
                </div>
                <span className="text-xs text-[#888888]">+more</span>
              </div>

              {/* Powered By */}
              <p className="text-center text-xs text-[#888888] mt-5">
                Powered by <a href={pricing.payment_gateway === "razorpay" ? "https://razorpay.com" : "https://paypal.com"} target="_blank" rel="noopener noreferrer" className="text-[#E8A0BF] hover:underline">{getPaymentGateway()}</a>
              </p>
            </div>
          </motion.div>
        </div>

        {/* Footer Disclaimer */}
        <div className="mt-10 text-center text-xs text-[#888888]">
          <p>
            By completing this purchase, you agree to our{" "}
            <a href="/terms" className="text-[#E8A0BF] hover:underline">Terms & Conditions</a>
            {" "}and{" "}
            <a href="/privacy" className="text-[#E8A0BF] hover:underline">Privacy Policy</a>.
          </p>
          <p className="mt-1">This certificate is for entertainment and novelty purposes only.</p>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
